import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { CustomerOrderStatus, OrderDTO, PaymentSummaryDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';
import { apiFetch, apiFetchBlob } from '../api/client.js';
import { useRealtimeSync } from '../realtime/useRealtimeSync.js';
import { downloadBlob } from '../lib/download.js';
import { labels } from '../labels.js';
import { ClientHeader } from '../components/ClientHeader.js';
import { ConnectionBanner } from '../components/ConnectionBanner.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon, type IconName } from '../components/Icon.js';
import { OrderStatusTimeline } from '../components/OrderStatusTimeline.js';
import { PaymentPanel } from '../components/PaymentPanel.js';
import styles from './OrderTrackPage.module.css';

const STATUS_ICON: Record<CustomerOrderStatus, IconName> = {
  PENDING: 'receipt',
  PREPARING: 'utensils',
  READY: 'bag',
  SERVED: 'check-circle',
  CANCELLED: 'close',
};

export function OrderTrackPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<OrderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [paid, setPaid] = useState(false);
  const [payment, setPayment] = useState<PaymentSummaryDTO | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const token = localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);

  const load = useCallback(() => {
    if (!token || !id) return;
    return apiFetch<OrderDTO>(`/public/orders/${id}`)
      .then((o) => { setOrder(o); setPayment(o.payment); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  const { socket, connected } = useRealtimeSync(token, load);

  useEffect(() => {
    if (!socket) return;
    const onStatus = (e: { payload: { orderId: string } }) => {
      if (e.payload.orderId === id) load();
    };
    // Payer ferme la session (docs/07, section 2.4). Ce n'est pas une
    // eviction : c'est precisement l'instant ou le recu devient telechargeable,
    // et l'API laisse la session close en lecture. On recharge, on ne masque pas.
    const onPaymentVerified = (e: { payload: { payment: PaymentSummaryDTO; orderId: string } }) => {
      if (e.payload.orderId !== id) return;
      setPayment(e.payload.payment);
      load();
    };
    const onClosed = (e: { payload: { reason: string } }) => {
      if (e.payload.reason === 'PAYMENT_COMPLETED') {
        setPaid(true);
        load();
        return;
      }
      setSessionClosed(true);
    };
    socket.on('order:status_changed' as never, onStatus as never);
    socket.on('order:updated' as never, onStatus as never);
    socket.on('payment:verified' as never, onPaymentVerified as never);
    socket.on('session:closed' as never, onClosed as never);
    return () => {
      socket.off('order:status_changed' as never, onStatus as never);
      socket.off('order:updated' as never, onStatus as never);
      socket.off('payment:verified' as never, onPaymentVerified as never);
      socket.off('session:closed' as never, onClosed as never);
    };
  }, [socket, id, load]);

  if (sessionClosed) {
    return (
      <main className={`client-screen ${styles.centered}`}>
        <EmptyState icon="scan" title={labels.errors.sessionClosed} text={labels.errors.thanks}>
          <button onClick={() => { localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY); window.location.href = '/scan'; }} className="btn-primary btn-lg">
            <Icon name="scan" size={20} />
            {labels.app.rescan}
          </button>
        </EmptyState>
      </main>
    );
  }

  if (loading) {
    return (
      <div className={`client-screen ${styles.screen}`}>
        <ClientHeader title={labels.order.tracking} backTo="/commandes" backLabel={labels.order.allOrders} />
        <main className={styles.main}>
          <div className="skeleton" style={{ height: '14rem', borderRadius: 'var(--radius-xl)' }} />
          <div className="skeleton" style={{ height: '10rem', borderRadius: 'var(--radius-lg)' }} />
        </main>
      </div>
    );
  }

  if (!order || loadError) {
    return (
      <main className={`client-screen ${styles.centered}`}>
        <EmptyState icon="receipt" text={labels.order.notFound} role="alert">
          <Link to="/commandes" className="btn-primary">{labels.order.allOrders}</Link>
        </EmptyState>
      </main>
    );
  }

  const cs = order.customerStatus;
  const isCancelled = cs === 'CANCELLED';
  const isCompleted = order.status === 'COMPLETED';

  const downloadReceipt = async () => {
    setDownloadError(false);
    try {
      const blob = await apiFetchBlob(`/public/orders/${order.id}/invoice.pdf`);
      downloadBlob(blob, `recu-${order.number}.pdf`);
    } catch {
      setDownloadError(true);
    }
  };

  return (
    <div className={`client-screen ${styles.screen}`}>
      <ConnectionBanner connected={connected} />
      <ClientHeader
        title={labels.order.number(order.number)}
        subtitle={labels.app.tableLabel(order.table.label)}
        backTo="/commandes"
        backLabel={labels.order.allOrders}
      />

      <main className={styles.main}>
        {paid && (
          <p role="status" className="notice notice--success" style={{ margin: 0 }}>
            <Icon name="check-circle" size={18} />
            {labels.errors.sessionClosedByPayment}
          </p>
        )}

        {isCancelled ? (
          <section className={`${styles.status} ${styles.statusCancelled}`} aria-live="polite">
            <span className={styles.statusIcon}><Icon name="close" size={28} /></span>
            <h2 className={styles.statusTitle}>{labels.customerStatus.CANCELLED}</h2>
            <p className={styles.statusText}>{order.cancelReason ?? labels.customerStatusDetail.CANCELLED}</p>
          </section>
        ) : (
          <section className={styles.status} aria-live="polite">
            <span className={styles.live}>
              <span className={styles.liveDot} aria-hidden="true" />
              {isCompleted ? labels.order.completed : labels.order.live}
            </span>
            <span className={styles.statusIcon}><Icon name={isCompleted ? 'check-circle' : STATUS_ICON[cs]} size={28} /></span>
            <h2 className={styles.statusTitle}>{isCompleted ? labels.order.completed : labels.customerStatus[cs]}</h2>
            <p className={styles.statusText}>{labels.customerStatusDetail[cs]}</p>
            <div className={styles.timeline}>
              <OrderStatusTimeline status={cs} />
            </div>
          </section>
        )}

        {/* Le convive declenche son paiement ; la confirmation vient du
            prestataire ou du personnel. Voir docs/adr/0012 et 0018. */}
        {!isCancelled && order.status === 'SERVED' && (
          <PaymentPanel
            orderId={order.id}
            totalMinor={order.totalMinor}
            currency={order.currency}
            payment={payment}
            onDeclared={setPayment}
          />
        )}

        <section className={styles.summary}>
          <h2 className={styles.summaryTitle}>{labels.order.summary}</h2>
          <ul className={styles.items}>
            {order.items.map((item) => (
              <li key={item.id}>
                <span className={styles.qty}>{item.quantity}</span>
                <span className="grow">
                  {item.nameSnapshot}
                  {item.note && <span className={styles.itemNote}>{item.note}</span>}
                </span>
                <span className="tabular">{formatMoney(item.lineTotalMinor, order.currency)}</span>
              </li>
            ))}
          </ul>
          <div className={styles.totalRow}>
            <span>{labels.cart.total}</span>
            <strong>{formatMoney(order.totalMinor, order.currency)}</strong>
          </div>
        </section>

        {isCompleted && (
          <>
            <button onClick={() => void downloadReceipt()} className="btn-primary btn-block btn-lg">
              <Icon name="download" size={20} />
              {labels.order.downloadReceipt}
            </button>
            {downloadError && (
              <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
                <Icon name="alert" size={18} />
                {labels.errors.generic}
              </p>
            )}
          </>
        )}

        <Link to="/menu" className="btn-secondary btn-block btn-lg">
          <Icon name="plus" size={20} />
          {labels.order.addAnother}
        </Link>
      </main>
    </div>
  );
}
